import { Link } from "react-router-dom";
import { ArrowRightIcon, TreesIcon } from "lucide-react";
import Image from "src/components/elements/Image";
import Plane from "../../assets/images/plane.svg";
import Train from "../../assets/images/train.svg";
import Temparature from "../../assets/images/temparature.svg";
import Tree from "../../assets/images/tree.svg";
import Tree2 from "../../assets/images/tree-2.svg";
import City from "../../assets/images/city.svg";
import Clock from "../../assets/images/clock.svg";
import Earth from "../../assets/images/earth.svg";
import Luggage from "../../assets/images/luggage.svg";
import RelaxTravel from "../../assets/images/relax-travel.svg";
import Scenery from "../../assets/images/scenery.svg";

const TravelPlanet = () => {
  return (
    <div className="mx-auto max-w-[1300px] px-8 pb-10 pt-12">
      {/* Hero Header */}
      <div className="mb-16 text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-semibold uppercase text-emerald-500 shadow">
          <TreesIcon
            size={20}
            color="currentColor"
          />
          Sustainable Travel
        </div>
        <h1 className="text-5xl font-bold leading-tight text-[#572f5f] md:text-6xl">
          Travel Today.
          <br />
          Protect Tomorrow.
        </h1>
        <div className="mx-auto mt-6 max-w-3xl text-xl text-gray-600">We believe responsible travel and environmental stewardship go hand in hand. Every journey you take with us helps build a greener future.</div>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        {/* Main Content */}
        <div className="space-y-12 lg:col-span-8">
          {/* Did You Know? - Enhanced with more facts */}
          <div className="rounded-2xl bg-white p-10 shadow">
            <h2 className="mb-8 text-3xl font-extrabold tracking-wide text-gray-800">Did You Know?</h2>
            <div className="grid gap-12 md:grid-cols-2">
              <div className="relative">
                <div className="mb-3 flex aspect-square w-16">
                  <Image
                    src={Plane}
                    alt={""}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-cover"}
                  />
                </div>
                <div className="text-lg font-semibold">Aviation contributes ~2.5% of global CO₂ emissions.</div>
                <div className="mt-2 text-sm text-gray-600">Business travel accounts for a significant portion of this impact.</div>
              </div>
              <div className="relative">
                <div className="mb-3 flex aspect-square w-16">
                  <Image
                    src={Temparature}
                    alt={""}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-cover"}
                  />
                </div>
                <div className="text-lg font-semibold">One transatlantic round-trip flight emits more CO₂ than an average person in many countries does in a year.</div>
              </div>
              <div className="relative">
                <div className="mb-3 flex aspect-square w-16">
                  <Image
                    src={Train}
                    alt={""}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-cover"}
                  />
                </div>
                <div className="text-lg font-semibold">Train travel emits up to 90% less CO₂ than flying the same distance.</div>
              </div>
              <div className="relative">
                <div className="mb-3 flex aspect-square w-16">
                  <Image
                    src={Tree}
                    alt={""}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-cover"}
                  />
                </div>
                <div className="text-lg font-semibold">Planting trees is one of the most effective ways to offset carbon and restore ecosystems.</div>
              </div>
            </div>
          </div>

          {/* Travel Green Section */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-700 p-12 text-white shadow">
            <div className="relative z-10">
              <h2 className="mb-6 text-4xl font-semibold">Travel Green with Confidence</h2>
              <div className="max-w-lg text-lg leading-relaxed opacity-95">Choose low-emission options, offset your carbon footprint, support eco-certified hotels, and travel with purpose. Small decisions today create a lasting positive impact.</div>
              <Link
                to="/travel/dashboard"
                className="mt-8 inline-flex h-14 items-center gap-4 rounded-2xl bg-white px-6 text-lg font-semibold text-emerald-800 transition-all hover:bg-emerald-50 hover:shadow-xl active:scale-95">
                Discover Green Travel Options
                <ArrowRightIcon
                  size={24}
                  color="currentColor"
                />
              </Link>
            </div>
            {/* Subtle background decoration */}
            <div className="absolute -bottom-10 -right-10 aspect-square h-full opacity-50">
              <Image
                src={Tree2}
                alt={""}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-cover"}
              />
            </div>
          </div>

          {/* Why Train Instead of Plane? - More detailed bullets */}
          <div className="rounded-2xl bg-white p-10 shadow">
            <h2 className="mb-8 text-3xl font-extrabold tracking-wide text-gray-800">Why Choose Train Over Plane?</h2>
            <div className="grid gap-12 md:grid-cols-2">
              {[
                { image: Earth, text: "Reduces your carbon footprint by up to 90%" },
                { image: Clock, text: "No lengthy airport security or boarding waits" },
                { image: City, text: "Train stations are located in city centers — no extra transfers" },
                { image: Scenery, text: "Enjoy breathtaking scenery throughout your journey" },
                { image: Luggage, text: "More space for luggage and comfortable working environment" },
                { image: RelaxTravel, text: "Relaxing and less stressful travel experience" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="relative">
                  <div className="mb-3 flex aspect-square w-16">
                    <Image
                      src={item.image}
                      alt={""}
                      width={"100%"}
                      height={"100%"}
                      effect={"blur"}
                      classes={"object-cover"}
                    />
                  </div>
                  <div className="text-lg font-semibold">{item.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Sustainability Partnership */}
          <div className="rounded-2xl bg-white p-10 shadow">
            <div className="space-y-8">
              <div className="mb-8">
                <h2 className="text-3xl font-extrabold tracking-wide text-gray-800">Sustainability Partnership</h2>
                <div className="text-xl font-medium text-green-500">Partnered with Trees4Travel</div>
              </div>
              <div className="w-full overflow-hidden rounded-2xl">
                <Image
                  src={"https://img.freepik.com/free-photo/3d-travel-icon-with-airplane_23-2151037312.jpg"}
                  alt={"Airplane vs Trees - Carbon Offset"}
                  width={"100%"}
                  height={"100%"}
                  effect={"blur"}
                  classes={"object-cover"}
                />
              </div>
              <div className="text-lg leading-relaxed text-gray-600">
                For every trip booked through us, we partner with <span className="font-semibold text-emerald-700">Trees4Travel</span> to plant trees that actively offset your carbon emissions and restore vital ecosystems.
              </div>
              <button className="mt-10 flex h-14 items-center gap-4 rounded-2xl bg-emerald-700 px-6 text-lg font-semibold text-white shadow-lg transition-all hover:bg-emerald-800 active:scale-95">
                Speak to your Account Manager
                <ArrowRightIcon
                  size={24}
                  color="currentColor"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Visual Gallery */}
        <div className="lg:col-span-4">
          <div className="space-y-8">
            {/* Hero Tree Planting Image */}
            <div className="overflow-hidden rounded-2xl bg-white shadow">
              <Image
                src={"https://img.freepik.com/free-photo/volunteers-little-kid-planting-tree-covering-hole-ground_482257-91057.jpg"}
                alt={"Volunteers planting trees for sustainability"}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-cover"}
              />
              <div className="p-6">
                <div className="text-base font-semibold text-emerald-700">One tree planted per journey</div>
                <div className="text-sm text-gray-500">Helping reforest the planet, one trip at a time</div>
              </div>
            </div>

            {/* Hands Planting Sapling */}
            <div className="overflow-hidden rounded-2xl bg-white shadow">
              <Image
                src={"https://img.freepik.com/free-photo/women-working-greenhouse-with-flowerpots_1157-30869.jpg"}
                alt={"Hands planting a young tree sapling"}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-cover"}
              />
            </div>

            {/* Lush Forest Image */}
            <div className="overflow-hidden rounded-2xl bg-white shadow">
              <Image
                src={"https://img.freepik.com/free-photo/beautiful-shot-grassy-field-with-trees-fog_181624-8607.jpg"}
                alt={"Lush green forest with sunlight"}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-cover"}
              />
              <div className="p-6 text-center text-base italic text-gray-500">Nature is healing — and we’re proud to be part of it.</div>
            </div>

            {/* Child Hands Planting */}
            <div className="overflow-hidden rounded-2xl bg-white shadow">
              <Image
                src={"https://img.freepik.com/free-photo/close-up-transplanting-process-plants_23-2149080722.jpg"}
                alt={"Child planting a sapling"}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-cover"}
              />
            </div>

            <div className="inline-block max-w-2xl rounded-2xl bg-white px-8 py-8 shadow">
              <div className="text-2xl font-medium text-gray-800">Ready to make your next trip more sustainable?</div>
              <div className="mt-4 text-gray-600">Contact your dedicated Account Manager today to explore green travel options and carbon offsetting.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelPlanet;
